import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { getToken } from 'src/utils/Common';
import { getSelectedServiceShop } from './api';

const DEFAULT_HUB_PATH = '/hubs/technician-location';
const DEFAULT_EVENT_NAME = 'TechnicianLocationUpdated';
const DEFAULT_SUBSCRIBE_METHOD = 'SubscribeTechnician';
const DEFAULT_UNSUBSCRIBE_METHOD = 'UnsubscribeTechnician';
const DEFAULT_REQUEST_METHOD = 'GetTechnicianLocation';
const START_RETRY_DELAYS = [1000, 2000, 5000, 10000, 20000, 30000];
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 20000, 30000];

export const TECH_LOCATION_STATUS = Object.freeze({
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  LIVE: 'live',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
});

const normalizeIdentifier = (value) => String(value ?? '').trim().toLocaleLowerCase('tr');

const withSelectedServiceShop = (hubUrl) => {
  const serviceShopId = getSelectedServiceShop()?.id;
  if (!serviceShopId) return hubUrl;

  try {
    const url = new URL(hubUrl, window.location.origin);
    url.searchParams.set('aronRotamServiceShopId', String(serviceShopId));
    return url.toString();
  } catch {
    const separator = String(hubUrl).includes('?') ? '&' : '?';
    return `${hubUrl}${separator}aronRotamServiceShopId=${encodeURIComponent(serviceShopId)}`;
  }
};

const firstDefined = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const parseCoordinate = (value) => {
  if (value === undefined || value === null || value === '') return null;
  let normalized = String(value).trim().replace(/\s+/g, '').replace(/[^\d.,+-]/g, '');
  const commaIndex = normalized.lastIndexOf(',');
  const dotIndex = normalized.lastIndexOf('.');
  if (commaIndex >= 0 && dotIndex >= 0) {
    normalized = commaIndex > dotIndex
      ? normalized.replace(/\./g, '').replace(',', '.')
      : normalized.replace(/,/g, '');
  } else if (commaIndex >= 0) {
    normalized = normalized.replace(',', '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCoordinates = (latitudeValue, longitudeValue) => {
  let latitude = parseCoordinate(latitudeValue);
  let longitude = parseCoordinate(longitudeValue);
  if (latitude == null || longitude == null) return null;

  // Türkiye saha verisinde alanların ters gönderildiği kayıtları güvenli şekilde düzelt.
  if (latitude >= 25 && latitude <= 45 && longitude >= 35 && longitude <= 43 && (latitude < 35 || latitude > 43)) {
    [latitude, longitude] = [longitude, latitude];
  }
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || latitude === 0 || longitude === 0) return null;
  return { latitude, longitude };
};

const parseTimestamp = (value) => {
  if (value === undefined || value === null || value === '') return new Date().toISOString();
  const numeric = Number(value);
  const timestamp = Number.isFinite(numeric)
    ? new Date(numeric < 100000000000 ? numeric * 1000 : numeric)
    : new Date(value);
  return Number.isNaN(timestamp.getTime()) ? new Date().toISOString() : timestamp.toISOString();
};

const unwrapPayload = (value) => {
  let payload = value;
  if (typeof payload === 'string') {
    try { payload = JSON.parse(payload); } catch { return null; }
  }
  if (!payload || typeof payload !== 'object') return null;
  if (Array.isArray(payload)) return payload;
  const nested = payload.location || payload.Location || payload.data || payload.Data;
  if (Array.isArray(nested)) return nested;
  return nested && typeof nested === 'object' && !Array.isArray(nested)
    ? { ...payload, ...nested }
    : payload;
};

export const normalizeTechnicianLocation = (...args) => {
  let payload = unwrapPayload(args[0]);
  if (Array.isArray(payload)) {
    const normalizedItems = payload.map(item => normalizeTechnicianLocation(item)).filter(Boolean);
    return normalizedItems.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp))[0] || null;
  }
  if (!payload && args.length >= 3) {
    payload = {
      technicianCode: args[0],
      latitude: args[1],
      longitude: args[2],
      timestamp: args[3],
      accuracy: args[4],
    };
  }
  if (!payload) return null;

  const coordinates = normalizeCoordinates(
    firstDefined(payload, ['latitude', 'Latitude', 'lat', 'Lat', 'technicianLatitude', 'TechnicianLatitude']),
    firstDefined(payload, ['longitude', 'Longitude', 'lng', 'Lng', 'lon', 'Lon', 'technicianLongitude', 'TechnicianLongitude']),
  );
  if (!coordinates) return null;

  const accuracy = Number(firstDefined(payload, ['accuracy', 'Accuracy', 'accuracyMeters', 'AccuracyMeters']));
  const speed = Number(firstDefined(payload, ['speed', 'Speed', 'speedKmh', 'SpeedKmh']));
  const heading = Number(firstDefined(payload, ['heading', 'Heading', 'bearing', 'Bearing']));
  const onlineValue = firstDefined(payload, ['isOnline', 'IsOnline', 'online', 'Online']);
  return {
    technicianCode: String(firstDefined(payload, [
      'technicianCode', 'TechnicianCode', 'techCode', 'TechCode', 'userTechCode', 'UserTechCode',
      'technicianId', 'TechnicianId', 'userId', 'UserId',
    ]) ?? '').trim(),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    timestamp: parseTimestamp(firstDefined(payload, [
      'timestamp', 'Timestamp', 'recordedAt', 'RecordedAt', 'occurredAt', 'OccurredAt',
      'locationTime', 'LocationTime', 'createdAt', 'CreatedAt', 'createDate', 'CreateDate',
    ])),
    accuracy: Number.isFinite(accuracy) && accuracy >= 0 ? accuracy : null,
    speed: Number.isFinite(speed) && speed >= 0 ? speed : null,
    heading: Number.isFinite(heading) ? heading : null,
    isOnline: ![false, 0, '0', 'false', 'False', 'FALSE'].includes(onlineValue),
  };
};

const asPositiveNumber = (value, fallback, min, max) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};

const parseTransport = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s_-]/g, '');
  if (normalized === 'websocket' || normalized === 'websockets') return HttpTransportType.WebSockets;
  if (normalized === 'serversentevent' || normalized === 'serversentevents' || normalized === 'sse') return HttpTransportType.ServerSentEvents;
  if (normalized === 'longpolling' || normalized === 'polling') return HttpTransportType.LongPolling;
  return null;
};

export const getDefaultTechnicianLocationHubUrl = (apiUrl = process.env.REACT_APP_API_URL) => {
  const rawUrl = String(apiUrl || '').trim();
  if (!rawUrl) return DEFAULT_HUB_PATH;

  try {
    const url = new URL(rawUrl);
    const apiBasePath = url.pathname.replace(/\/api\/aron\/?$/i, '').replace(/\/$/, '');
    url.pathname = `${apiBasePath}${DEFAULT_HUB_PATH}`;
    url.search = '';
    url.hash = '';
    return url.toString().replace(/\/$/, '');
  } catch {
    const apiBase = rawUrl.replace(/\/$/, '').replace(/\/api\/aron$/i, '');
    return `${apiBase}${DEFAULT_HUB_PATH}`;
  }
};

export const readTechnicianLocationRuntimeConfig = () => {
  const root = typeof window !== 'undefined' ? (window.APP_CONFIG || {}) : {};
  const nested = root.techLocationSignalR || {};
  const skipNegotiationValue = nested.skipNegotiation ??
    root.techLocationSkipNegotiation ??
    process.env.REACT_APP_TECH_LOCATION_SKIP_NEGOTIATION;
  const hubUrl = nested.hubUrl || root.techLocationHubUrl || process.env.REACT_APP_TECH_LOCATION_HUB_URL || getDefaultTechnicianLocationHubUrl();
  const configuredTransport = parseTransport(
    nested.transport ?? root.techLocationTransport ?? process.env.REACT_APP_TECH_LOCATION_TRANSPORT,
  );
  const skipNegotiation = skipNegotiationValue === true || String(skipNegotiationValue).toLowerCase() === 'true';
  return {
    hubUrl,
    eventName: nested.eventName || root.techLocationEventName || process.env.REACT_APP_TECH_LOCATION_EVENT_NAME || DEFAULT_EVENT_NAME,
    subscribeMethod: nested.subscribeMethod ?? root.techLocationSubscribeMethod ?? process.env.REACT_APP_TECH_LOCATION_SUBSCRIBE_METHOD ?? DEFAULT_SUBSCRIBE_METHOD,
    unsubscribeMethod: nested.unsubscribeMethod ?? root.techLocationUnsubscribeMethod ?? process.env.REACT_APP_TECH_LOCATION_UNSUBSCRIBE_METHOD ?? DEFAULT_UNSUBSCRIBE_METHOD,
    requestMethod: nested.requestMethod ?? root.techLocationRequestMethod ?? process.env.REACT_APP_TECH_LOCATION_REQUEST_METHOD ?? DEFAULT_REQUEST_METHOD,
    argumentName: nested.argumentName || root.techLocationArgumentName || process.env.REACT_APP_TECH_LOCATION_ARGUMENT_NAME || '',
    // ARR/session-affinity cookies must be sent during cross-origin negotiate calls.
    withCredentials: nested.withCredentials !== false,
    // WebSockets-only mode is opt-in. The default negotiation path preserves
    // SSE/Long Polling fallback when WebSockets is unavailable.
    transport: configuredTransport,
    skipNegotiation: skipNegotiation && (configuredTransport == null || configuredTransport === HttpTransportType.WebSockets),
    minUpdateIntervalMs: asPositiveNumber(nested.minUpdateIntervalMs, 1000, 250, 5000),
    uiRefreshIntervalMs: asPositiveNumber(nested.uiRefreshIntervalMs, 5000, 1000, 30000),
    staleAfterMs: asPositiveNumber(nested.staleAfterMs, 360000, 15000, 3600000),
    serverTimeoutMs: asPositiveNumber(nested.serverTimeoutMs, 30000, 10000, 120000),
    keepAliveIntervalMs: asPositiveNumber(nested.keepAliveIntervalMs, 15000, 5000, 60000),
    locationPollIntervalMs: asPositiveNumber(nested.locationPollIntervalMs, 15000, 5000, 60000),
  };
};

const getAccessToken = async () => {
  const root = typeof window !== 'undefined' ? (window.APP_CONFIG || {}) : {};
  const tokenFactory = root.techLocationSignalR?.accessTokenFactory || root.getTechLocationAccessToken;
  if (typeof tokenFactory === 'function') {
    try { return String((await tokenFactory()) || ''); } catch { return ''; }
  }
  return String(getToken() || '');
};

const withTimeout = (promise, timeoutMs) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('SignalR işlemi zaman aşımına uğradı.')), timeoutMs);
  Promise.resolve(promise).then(
    value => { clearTimeout(timer); resolve(value); },
    error => { clearTimeout(timer); reject(error); },
  );
});

const getErrorMessage = (error) => {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Canlı konum bağlantısı kurulamadı.';
  if (/unauthorized|401|403/i.test(message)) return 'Canlı konum bağlantısı için yetki doğrulanamadı.';
  if (/failed to fetch|network|negotiation|timeout|zaman aşımı/i.test(message)) return 'Canlı konum sunucusuna ulaşılamadı; yeniden deneniyor.';
  return 'Canlı konum bağlantısı kesildi; yeniden deneniyor.';
};

const buildArgument = (config, technicianKey) => (
  config.argumentName ? { [config.argumentName]: technicianKey } : technicianKey
);

export default function useTechnicianLiveLocation({
  enabled,
  technician,
  onLocation,
  onClear,
}) {
  const [tracking, setTracking] = useState(false);
  const [status, setStatus] = useState(TECH_LOCATION_STATUS.IDLE);
  const [lastLocation, setLastLocation] = useState(null);
  const [error, setError] = useState('');

  const mountedRef = useRef(true);
  const activeRef = useRef(false);
  const generationRef = useRef(0);
  const connectionRef = useRef(null);
  const eventHandlerRef = useRef(null);
  const retryTimerRef = useRef(0);
  const requestTimerRef = useRef(0);
  const flushTimerRef = useRef(0);
  const pendingLocationRef = useRef(null);
  const lastLocationRef = useRef(null);
  const lastDeliveryAtRef = useRef(0);
  const lastUiRefreshAtRef = useRef(0);
  const hasFocusedRef = useRef(false);
  const startingRef = useRef(false);
  const activeTechnicianKeyRef = useRef('');
  const configRef = useRef(readTechnicianLocationRuntimeConfig());
  const onLocationRef = useRef(onLocation);
  const onClearRef = useRef(onClear);
  const targetKeysRef = useRef(new Set());

  const technicianKey = String(technician?.trackingKey || technician?.techCode || technician?.userTechCode || '').trim();
  targetKeysRef.current = new Set([
    technicianKey,
    technician?.techCode,
    technician?.userTechCode,
  ].map(normalizeIdentifier).filter(Boolean));
  onLocationRef.current = onLocation;
  onClearRef.current = onClear;

  const safeSetStatus = useCallback((value) => {
    if (mountedRef.current) setStatus(value);
  }, []);

  const safeSetError = useCallback((value) => {
    if (mountedRef.current) setError(value);
  }, []);

  const deliverPendingLocation = useCallback(() => {
    flushTimerRef.current = 0;
    const location = pendingLocationRef.current;
    if (!location || !activeRef.current) return;
    pendingLocationRef.current = null;
    lastLocationRef.current = location;
    lastDeliveryAtRef.current = Date.now();
    const focus = !hasFocusedRef.current;
    hasFocusedRef.current = true;
    try { onLocationRef.current?.(location, { focus }); } catch { /* harita hatası bağlantıyı etkilemez */ }
    safeSetStatus(TECH_LOCATION_STATUS.LIVE);
    safeSetError('');

    const now = Date.now();
    if (!lastUiRefreshAtRef.current || now - lastUiRefreshAtRef.current >= configRef.current.uiRefreshIntervalMs) {
      lastUiRefreshAtRef.current = now;
      if (mountedRef.current) setLastLocation(location);
    }
  }, [safeSetError, safeSetStatus]);

  const enqueueLocation = useCallback((location) => {
    if (!location || !activeRef.current) return;
    const incomingCode = normalizeIdentifier(location.technicianCode);
    if (incomingCode && !targetKeysRef.current.has(incomingCode)) return;

    const previous = pendingLocationRef.current || lastLocationRef.current;
    if (previous) {
      const previousTime = Date.parse(previous.timestamp);
      const incomingTime = Date.parse(location.timestamp);
      if (Number.isFinite(previousTime) && Number.isFinite(incomingTime) && incomingTime < previousTime) return;
      if (previous.latitude === location.latitude && previous.longitude === location.longitude && previousTime === incomingTime) return;
    }

    const timestampMs = Date.parse(location.timestamp);
    pendingLocationRef.current = {
      ...location,
      stale: location.isOnline === false || (Number.isFinite(timestampMs) && Date.now() - timestampMs > configRef.current.staleAfterMs),
    };
    if (flushTimerRef.current) return;
    const delay = Math.max(0, configRef.current.minUpdateIntervalMs - (Date.now() - lastDeliveryAtRef.current));
    flushTimerRef.current = window.setTimeout(deliverPendingLocation, delay);
  }, [deliverPendingLocation]);

  const stop = useCallback(() => {
    generationRef.current += 1;
    activeRef.current = false;
    startingRef.current = false;
    window.clearTimeout(retryTimerRef.current);
    window.clearInterval(requestTimerRef.current);
    window.clearTimeout(flushTimerRef.current);
    retryTimerRef.current = 0;
    requestTimerRef.current = 0;
    flushTimerRef.current = 0;
    pendingLocationRef.current = null;
    lastLocationRef.current = null;
    lastDeliveryAtRef.current = 0;
    lastUiRefreshAtRef.current = 0;
    hasFocusedRef.current = false;

    const connection = connectionRef.current;
    const eventHandler = eventHandlerRef.current;
    const config = configRef.current;
    const activeKey = activeTechnicianKeyRef.current;
    connectionRef.current = null;
    eventHandlerRef.current = null;
    activeTechnicianKeyRef.current = '';

    if (connection) {
      if (eventHandler && config.eventName) connection.off(config.eventName, eventHandler);
      if (connection.state === HubConnectionState.Connected && config.unsubscribeMethod && activeKey) {
        withTimeout(connection.invoke(config.unsubscribeMethod, buildArgument(config, activeKey)), 3000).catch(() => {});
      }
      Promise.resolve(connection.stop()).catch(() => {});
    }
    try { onClearRef.current?.(); } catch { /* harita temizliği bağlantıyı etkilemez */ }
    if (mountedRef.current) {
      setTracking(false);
      setStatus(TECH_LOCATION_STATUS.IDLE);
      setLastLocation(null);
      setError('');
    }
  }, []);

  const start = useCallback(() => {
    if (activeRef.current) return;
    if (!enabled || !technicianKey) {
      safeSetStatus(TECH_LOCATION_STATUS.ERROR);
      safeSetError('Teknisyen kodu bulunamadığı için canlı konum başlatılamadı.');
      return;
    }

    const config = readTechnicianLocationRuntimeConfig();
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    configRef.current = config;
    activeRef.current = true;
    activeTechnicianKeyRef.current = technicianKey;
    hasFocusedRef.current = false;
    lastLocationRef.current = null;
    pendingLocationRef.current = null;
    if (mountedRef.current) {
      setTracking(true);
      setStatus(TECH_LOCATION_STATUS.CONNECTING);
      setLastLocation(null);
      setError('');
    }

    const reconnectPolicy = {
      nextRetryDelayInMilliseconds: context => {
        if (!activeRef.current || generationRef.current !== generation) return null;
        const base = RECONNECT_DELAYS[Math.min(context.previousRetryCount, RECONNECT_DELAYS.length - 1)];
        return base + Math.floor(Math.random() * 500);
      },
    };
    const selectedTransport = config.transport ?? (config.skipNegotiation ? HttpTransportType.WebSockets : null);
    const connection = new HubConnectionBuilder()
      .withUrl(withSelectedServiceShop(config.hubUrl), {
        accessTokenFactory: getAccessToken,
        withCredentials: config.withCredentials,
        ...(selectedTransport != null ? { transport: selectedTransport } : {}),
        ...(config.skipNegotiation && selectedTransport === HttpTransportType.WebSockets ? {
          skipNegotiation: true,
        } : {}),
      })
      .withAutomaticReconnect(reconnectPolicy)
      .configureLogging(LogLevel.Warning)
      .build();
    connection.serverTimeoutInMilliseconds = config.serverTimeoutMs;
    connection.keepAliveIntervalInMilliseconds = config.keepAliveIntervalMs;
    connectionRef.current = connection;

    const eventHandler = (...args) => enqueueLocation(normalizeTechnicianLocation(...args));
    eventHandlerRef.current = eventHandler;
    connection.on(config.eventName, eventHandler);

    const subscribeAndRequest = async () => {
      if (!activeRef.current || generationRef.current !== generation || connection.state !== HubConnectionState.Connected) return false;
      const argument = buildArgument(config, technicianKey);
      if (config.subscribeMethod) {
        try {
          await withTimeout(connection.invoke(config.subscribeMethod, argument), 8000);
        } catch (subscriptionError) {
          console.warn('[TechnicianLiveLocation] Teknisyen aboneliği başlatılamadı.', subscriptionError);
          safeSetStatus(TECH_LOCATION_STATUS.ERROR);
          safeSetError('Teknisyen konum izleme yetkisi doğrulanamadı veya abonelik başlatılamadı.');
          return false;
        }
      }
      if (config.requestMethod) {
        try {
          const result = await withTimeout(connection.invoke(config.requestMethod, argument), 8000);
          enqueueLocation(normalizeTechnicianLocation(result));
        } catch (requestError) {
          console.warn('[TechnicianLiveLocation] Son teknisyen konumu alınamadı.', requestError);
        }
      }
      return true;
    };

    const startRequestPolling = () => {
      window.clearInterval(requestTimerRef.current);
      if (!config.requestMethod) return;
      requestTimerRef.current = window.setInterval(() => {
        if (!activeRef.current || generationRef.current !== generation || connection.state !== HubConnectionState.Connected) return;
        const argument = buildArgument(config, technicianKey);
        withTimeout(connection.invoke(config.requestMethod, argument), 8000)
          .then(result => enqueueLocation(normalizeTechnicianLocation(result)))
          .catch(() => {});
      }, config.locationPollIntervalMs);
    };

    const initializeSubscription = async () => {
      const subscriptionReady = await subscribeAndRequest();
      if (subscriptionReady) startRequestPolling();
      return subscriptionReady;
    };

    const scheduleStartRetry = (attempt, attemptStart) => {
      if (!activeRef.current || generationRef.current !== generation) return;
      window.clearTimeout(retryTimerRef.current);
      const base = START_RETRY_DELAYS[Math.min(attempt, START_RETRY_DELAYS.length - 1)];
      retryTimerRef.current = window.setTimeout(() => attemptStart(attempt + 1), base + Math.floor(Math.random() * 500));
    };

    const attemptStart = async (attempt = 0) => {
      if (!activeRef.current || generationRef.current !== generation || connectionRef.current !== connection || startingRef.current) return;
      if (connection.state !== HubConnectionState.Disconnected) return;
      startingRef.current = true;
      safeSetStatus(attempt > 0 ? TECH_LOCATION_STATUS.RECONNECTING : TECH_LOCATION_STATUS.CONNECTING);
      try {
        await connection.start();
        if (!activeRef.current || generationRef.current !== generation) {
          Promise.resolve(connection.stop()).catch(() => {});
          return;
        }
        safeSetStatus(TECH_LOCATION_STATUS.CONNECTED);
        safeSetError('');
        await initializeSubscription();
      } catch (startError) {
        if (activeRef.current && generationRef.current === generation) {
          safeSetStatus(TECH_LOCATION_STATUS.RECONNECTING);
          safeSetError(getErrorMessage(startError));
          scheduleStartRetry(attempt, attemptStart);
        }
      } finally {
        startingRef.current = false;
      }
    };

    connection.onreconnecting((connectionError) => {
      if (!activeRef.current || generationRef.current !== generation) return;
      safeSetStatus(TECH_LOCATION_STATUS.RECONNECTING);
      safeSetError(getErrorMessage(connectionError));
      window.clearInterval(requestTimerRef.current);
      requestTimerRef.current = 0;
    });
    connection.onreconnected(() => {
      if (!activeRef.current || generationRef.current !== generation) return;
      safeSetStatus(TECH_LOCATION_STATUS.CONNECTED);
      safeSetError('');
      initializeSubscription().catch(() => {});
    });
    connection.onclose((connectionError) => {
      if (!activeRef.current || generationRef.current !== generation) return;
      safeSetStatus(TECH_LOCATION_STATUS.RECONNECTING);
      safeSetError(getErrorMessage(connectionError));
      window.clearInterval(requestTimerRef.current);
      requestTimerRef.current = 0;
      scheduleStartRetry(0, attemptStart);
    });

    attemptStart();
  }, [enabled, enqueueLocation, safeSetError, safeSetStatus, technicianKey]);

  const toggle = useCallback(() => {
    if (activeRef.current) stop();
    else start();
  }, [start, stop]);

  const getCurrentLocation = useCallback(() => lastLocationRef.current, []);

  useEffect(() => {
    if (!enabled && activeRef.current) stop();
  }, [enabled, stop]);

  useEffect(() => {
    if (activeRef.current && activeTechnicianKeyRef.current && activeTechnicianKeyRef.current !== technicianKey) stop();
  }, [stop, technicianKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stop();
    };
  }, [stop]);

  return {
    tracking,
    status,
    lastLocation,
    error,
    start,
    stop,
    toggle,
    getCurrentLocation,
  };
}
